import { useAuth } from "@/auth/provider/AuthContext";
import {
  Account,
  Integration,
  OrgRef,
  UserRef,
  useViaductApi,
  Vendor,
} from "@/viaduct-api";
import { PropsWithChildren, useEffect, useState } from "react";
import { EntityImageContext } from "./entityImageContext";

class UrlCache extends Map<string, Promise<string | undefined>> {}

export const EntityImageProvider = ({ children }: PropsWithChildren) => {
  const { orgUser } = useAuth();
  const [userUrlCache] = useState<UrlCache>(new UrlCache());
  const [orgUrlCache] = useState<UrlCache>(new UrlCache());
  const [accountUrlCache] = useState<UrlCache>(new UrlCache());
  const [vendorUrlCache] = useState<UrlCache>(new UrlCache());
  const [integrationUrlCache] = useState<UrlCache>(new UrlCache());

  const { client } = useViaductApi();

  // Revoke all URLs on unmount
  useEffect(() => {
    return () => {
      userUrlCache.forEach((value) => {
        void value.then((url) => {
          if (url) {
            URL.revokeObjectURL(url);
          }
        });
      });
      orgUrlCache.forEach((value) => {
        void value.then((url) => {
          if (url) {
            URL.revokeObjectURL(url);
          }
        });
      });
      accountUrlCache.forEach((value) => {
        void value.then((url) => {
          if (url) {
            URL.revokeObjectURL(url);
          }
        });
      });
    };
  }, [accountUrlCache, orgUrlCache, userUrlCache]);

  const fetchUserImage = async ({ user }: { user: UserRef }) => {
    const result = await client.userImageGetUserImage({
      path: { user_id: user.id },
    });

    if (result.error) {
      // TODO
    }

    if (result.data) {
      const url = URL.createObjectURL(result.data as Blob);
      return url;
    }
  };

  const fetchOrgImage = async ({ org }: { org: OrgRef }) => {
    const result = await client.orgImageGetOrgImage({
      path: { org_id: org.id },
    });

    if (result.error) {
      // TODO
    }

    if (result.data) {
      const url = URL.createObjectURL(result.data as Blob);
      return url;
    }
  };

  const fetchAccountImage = async ({ account }: { account: Account }) => {
    if (!orgUser) {
      return;
    }

    const result = await client.orgAccountImageGetImage({
      path: { org_id: orgUser.org.id, account_id: account.id },
    });

    if (result.error) {
      // TODO
    }

    if (result.data) {
      const url = URL.createObjectURL(result.data as Blob);
      return url;
    }
  };

  const fetchVendorImage = async ({ vendor }: { vendor: Vendor }) => {
    const result = await client.vendorImageGetImage({
      path: { vendor_id: vendor.id },
    });

    if (result.error) {
      // TODO
    }

    if (result.data) {
      const url = URL.createObjectURL(result.data as Blob);
      return url;
    }
  };

  const fetchIntegrationImage = async ({
    integration,
  }: {
    integration: Integration;
  }) => {
    const result = await client.orgIntegrationImageGetImage({
      path: { integration_id: integration.id },
    });

    if (result.error) {
      // TODO
    }

    if (result.data) {
      const url = URL.createObjectURL(result.data as Blob);
      return url;
    }
  };

  const findUserImage = (user: UserRef) => {
    if (user.avatar_url) {
      return Promise.resolve(user.avatar_url);
    }

    if (!user.has_avatar) {
      return Promise.resolve(undefined);
    }

    if (userUrlCache.has(user.id)) {
      const existingValue = userUrlCache.get(user.id);
      if (existingValue) {
        return existingValue;
      }
    }

    const urlPromise = fetchUserImage({ user });
    userUrlCache.set(user.id, urlPromise);
    return urlPromise;
  };

  const findOrgImage = (org: OrgRef) => {
    if (org.logo_url) {
      return Promise.resolve(org.logo_url);
    }

    if (!org.has_logo) {
      return Promise.resolve(undefined);
    }

    if (orgUrlCache.has(org.id)) {
      const existingValue = orgUrlCache.get(org.id);
      if (existingValue) {
        return existingValue;
      }
    }

    const urlPromise = fetchOrgImage({ org });
    orgUrlCache.set(org.id, urlPromise);
    return urlPromise;
  };

  const findAccountImage = (account: Account) => {
    if (account.logo_url) {
      return Promise.resolve(account.logo_url);
    }

    if (!account.has_logo) {
      return Promise.resolve(undefined);
    }

    if (accountUrlCache.has(account.id)) {
      const existingValue = accountUrlCache.get(account.id);
      if (existingValue) {
        return existingValue;
      }
    }

    const urlPromise = fetchAccountImage({ account });
    accountUrlCache.set(account.id, urlPromise);
    return urlPromise;
  };

  const findVendorImage = (vendor: Vendor) => {
    if (vendor.logo_url) {
      return Promise.resolve(vendor.logo_url);
    }

    if (!vendor.has_logo) {
      return Promise.resolve(undefined);
    }

    if (vendorUrlCache.has(vendor.id)) {
      const existingValue = vendorUrlCache.get(vendor.id);
      if (existingValue) {
        return existingValue;
      }
    }

    const urlPromise = fetchVendorImage({ vendor });
    vendorUrlCache.set(vendor.id, urlPromise);
    return urlPromise;
  };

  const findIntegrationImage = (integration: Integration) => {
    if (integration.logo_url) {
      return Promise.resolve(integration.logo_url);
    }

    if (!integration.has_logo) {
      return Promise.resolve(undefined);
    }

    if (integrationUrlCache.has(integration.id)) {
      const existingValue = integrationUrlCache.get(integration.id);
      if (existingValue) {
        return existingValue;
      }
    }

    const urlPromise = fetchIntegrationImage({ integration });
    integrationUrlCache.set(integration.id, urlPromise);
    return urlPromise;
  };

  return (
    <EntityImageContext.Provider
      value={{
        findUserImage,
        findOrgImage,
        findAccountImage,
        findVendorImage,
        findIntegrationImage,
      }}
    >
      {children}
    </EntityImageContext.Provider>
  );
};
